import { Link } from "react-router-dom";
import { useState } from "react";
import { Youtube, Music2, Apple, Radio, Mail, ArrowRight, Disc3, Facebook } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/public/subscribe", {
        email,
        source: "footer",
      });
      if (data.already_subscribed && !data.pending_confirmation) {
        toast.success("You're already on the list. We see you.");
      } else {
        toast.success("Check your inbox — confirm your email to lock in.");
      }
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Subscription failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer
      className="border-t border-white/5 mt-32 pb-12"
      data-testid="site-footer"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            <h3 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light">
              <span style={{ color: "var(--gold)" }}>Independent</span>.
              <br />
              Authoritative.
              <br />
              <em className="font-light">Atmospheric.</em>
            </h3>
            <p className="text-sm text-white/50 max-w-md leading-relaxed">
              Nina Capone — pioneering independent artist, founder of In Da
              Streets Radio, and architect of Inspire Da Streets. Thirty years
              of work, one open door.
            </p>

            <form
              onSubmit={onSubscribe}
              className="surface p-5 lg:p-6 space-y-3"
              data-testid="footer-subscribe-form"
            >
              <p className="eyebrow">Inner Circle · Newsletter</p>
              <p className="text-sm text-white/65 leading-relaxed">
                First word on every release. Early access to OVA 40 deluxe
                edits & the upcoming{" "}
                <em className="italic" style={{ color: "var(--gold)" }}>
                  Audio Porn
                </em>{" "}
                drop.
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 flex-1"
                  data-testid="subscribe-email-input"
                />
                <Button
                  type="submit"
                  disabled={busy}
                  className="btn-gold rounded-none px-5 h-10 text-xs shrink-0"
                  data-testid="subscribe-submit"
                >
                  {busy ? "…" : <ArrowRight size={14} />}
                </Button>
              </div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/35">
                No spam. Just the work.
              </p>
            </form>
          </div>

          <div className="lg:col-span-2 space-y-3">
            <p className="eyebrow">Empire</p>
            <ul className="space-y-2 text-sm text-white/60">
              <li>
                <Link to="/radio" className="hover:text-[color:var(--gold)]">
                  Podcast
                </Link>
              </li>
              <li>
                <Link to="/music" className="hover:text-[color:var(--gold)]">
                  Music
                </Link>
              </li>
              <li>
                <Link to="/inspire" className="hover:text-[color:var(--gold)]">
                  Inspire Da Streets
                </Link>
              </li>
              <li>
                <Link to="/shop" className="hover:text-[color:var(--gold)]">
                  Shop
                </Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-[color:var(--gold)]">
                  About
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2 space-y-3">
            <p className="eyebrow">Connect</p>
            <ul className="space-y-2 text-sm text-white/60">
              <li>
                <a
                  href="https://indastreetsradio.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[color:var(--gold)]"
                  data-testid="footer-radio-link"
                >
                  indastreetsradio.com
                </a>
              </li>
              <li>
                <Link to="/about" className="hover:text-[color:var(--gold)]">
                  Press & Booking
                </Link>
              </li>
              <li>
                <Link to="/admin/login" className="hover:text-[color:var(--gold)]">
                  Admin
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3 space-y-3">
            <p className="eyebrow">Channels</p>
            <div className="flex items-center gap-4 text-white/60">
              <a
                href="https://open.spotify.com/artist/1aAI3BGhCIfzsuqx5kLh6O?si=VzVyKl2sRPS_5wiKhTU8iQ"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Spotify"
                className="hover:text-[color:var(--gold)]"
                data-testid="footer-spotify"
              >
                <Music2 size={18} />
              </a>
              <a
                href="https://music.apple.com/us/artist/nina-capone/296313757"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Apple Music"
                className="hover:text-[color:var(--gold)]"
                data-testid="footer-apple"
              >
                <Apple size={18} />
              </a>
              <a
                href="https://www.youtube.com/@indastreetsradio/releases"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="hover:text-[color:var(--gold)]"
                data-testid="footer-youtube"
              >
                <Youtube size={18} />
              </a>
              <a
                href="https://www.reverbnation.com/ninacapone"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Reverbnation"
                className="hover:text-[color:var(--gold)]"
                data-testid="footer-reverbnation"
              >
                <Radio size={18} />
              </a>
              <a
                href="https://www.n1m.com/ninacapone"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="N1M (NumberOneMusic)"
                title="N1M · NumberOneMusic"
                className="inline-flex items-center gap-1 hover:text-[color:var(--gold)]"
                data-testid="footer-n1m"
              >
                <Disc3 size={18} />
                <span className="text-[10px] uppercase tracking-[0.18em] font-medium">N1M</span>
              </a>
              <a
                href="https://www.facebook.com/ncapone1"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="hover:text-[color:var(--gold)]"
                data-testid="footer-facebook"
              >
                <Facebook size={18} />
              </a>
              <a
                href="mailto:bookings@ninacapone.com"
                aria-label="Email"
                className="hover:text-[color:var(--gold)]"
                data-testid="footer-email"
              >
                <Mail size={18} />
              </a>
            </div>
            <p className="text-xs text-white/40 pt-4">
              For booking & press: bookings@ninacapone.com
            </p>
          </div>
        </div>

        {/* Recognition badge — Influential Women */}
        <div
          className="mt-20 flex flex-col items-center text-center"
          data-testid="footer-recognition-badge"
        >
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/35 mb-5">
            Recognized By
          </p>
          <a
            href="https://influentialwomen.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block transition-transform duration-300 hover:-translate-y-0.5"
            data-testid="influential-women-badge-link"
          >
            <img
              src="https://cdn.bmapinc.com/configurations/config_68454009b3957.png"
              alt="Influential Women Badge"
              width={160}
              className="w-[160px] h-auto mx-auto"
              loading="lazy"
            />
          </a>
          <p className="mt-3 text-base" style={{ fontFamily: "Cambria, Times, serif" }}>
            <a
              href="https://influentialwomen.com/profile/jamilah-lawry"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[color:var(--gold)] hover:underline"
              data-testid="influential-women-profile-link"
            >
              Jamilah N. Lawry
            </a>
          </p>
        </div>

        <div className="divider-gold mt-20" />
        <div
          className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-6 text-[10px] uppercase tracking-[0.22em] text-white/40"
          data-testid="footer-legal-links"
        >
          <Link to="/terms" className="hover:text-[color:var(--gold)] transition" data-testid="footer-terms-link">
            Terms
          </Link>
          <span className="opacity-30">·</span>
          <Link to="/press" className="hover:text-[color:var(--gold)] transition" data-testid="footer-press-link">
            Press
          </Link>
          <span className="opacity-30">·</span>
          <Link to="/privacy" className="hover:text-[color:var(--gold)] transition" data-testid="footer-privacy-link">
            Privacy
          </Link>
          <span className="opacity-30">·</span>
          <Link to="/refund" className="hover:text-[color:var(--gold)] transition" data-testid="footer-refund-link">
            Refunds
          </Link>
          <span className="opacity-30">·</span>
          <Link to="/unsubscribe" className="hover:text-[color:var(--gold)] transition" data-testid="footer-unsubscribe-link">
            Unsubscribe
          </Link>
          <span className="opacity-30">·</span>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("nc:open-cookie-consent"))}
            className="uppercase tracking-[0.22em] hover:text-[color:var(--gold)] transition"
            data-testid="footer-cookie-preferences"
          >
            Cookie Preferences
          </button>
        </div>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-4 text-xs text-white/40">
          <p className="max-w-3xl leading-relaxed" data-testid="footer-trademark-notice">
            &copy; {new Date().getFullYear()} Jamilah N. Lawry, professionally
            known as Nina Capone. Nina Capone™ is a claimed trademark and service
            mark used in connection with music, live entertainment, media,
            appearances, merchandise, and related services. All rights reserved.
          </p>
          <p
            className="font-serif italic tracking-wide text-center md:text-right"
            data-testid="footer-loni-tribute"
          >
            In loving memory of{" "}
            <span style={{ color: "var(--gold)" }}>Loni Gamble</span> — first
            manager, forever in the credits.
          </p>
          <p className="font-serif italic tracking-wide">
            &ldquo;Respect the hustle. Honour the craft.&rdquo;
          </p>
        </div>
      </div>
    </footer>
  );
}
